import Vue from "vue";

const ACTIVE_TERMINAL_STATUSES = new Set(["complete", "failed", "cancelled"]);
const ACTIVE_ENTRY_TERMINAL_STATUSES = new Set(["COMPLETE", "ERROR", "FAILED", "CANCELLED", "TIMED_OUT", "ABANDONED"]);
const ACTIVE_ENTRY_ROW_CAP_PER_JOB = 500;
const ACTIVE_ENTRY_FADE_MS = 1200;
const ACTIVE_ENTRY_FAILURE_HOLD_MS = 10000;
const ACTIVE_ENTRY_SNAPSHOT_INTERVAL_MS = 250;

const state = Vue.observable({
  activeUpload: null,
  activeEntryActivity: {
    jobs: {},
  },
});

let manager = null;
let nextLocalUploadId = 1;
const activeEntryJobs = new Map();
let activeEntrySnapshotTimer = null;

function lightweightUploadState(job, updates = {}) {
  return {
    localUploadId: job.localUploadId,
    startedAt: job.startedAt || null,
    uploadJobId: job.uploadJobId || null,
    retryOfJobId: job.payload?.retryManifest?.retryOfJobId || null,
    folderId: job.folderId,
    status: job.status,
    currentItem: job.currentItem || 0,
    currentFilename: job.currentFilename || "",
    currentPhase: job.currentPhase || "",
    currentPhaseProgress: job.currentPhaseProgress || { current: 0, total: 0 },
    totalFolders: job.totalFolders || 0,
    foldersDone: job.foldersDone || 0,
    totalFiles: job.totalFiles || 0,
    progress: job.progress || 0,
    errorMessage: job.errorMessage || null,
    sensitiveDetections: job.sensitiveDetections || [],
    ...updates,
  };
}

function nowMs() {
  return Date.now();
}

function primitiveString(value, fallback = "") {
  if (value === null || value === undefined) {
    return fallback;
  }
  const type = typeof value;
  if (type === "string" || type === "number" || type === "boolean") {
    return String(value);
  }
  return fallback;
}

function primitiveNumber(value, fallback = null) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeEntryStatus(status) {
  const value = primitiveString(status, "IN_PROGRESS").toUpperCase();
  if (value === "QUEUED" || value === "WAITING") {
    return "PENDING";
  }
  if (value === "CREATING_FILE" || value === "CREATING_FOLDER" || value === "UPLOADING") {
    return "IN_PROGRESS";
  }
  if (value === "ERROR") {
    return "ERROR";
  }
  return value || "IN_PROGRESS";
}

function activeEntryKey(row) {
  return `${row.jobId}:${row.clientEntryId}`;
}

function totalHoldMs(status) {
  if (status === "COMPLETE") {
    return ACTIVE_ENTRY_FADE_MS;
  }
  if (ACTIVE_ENTRY_TERMINAL_STATUSES.has(status)) {
    return ACTIVE_ENTRY_FAILURE_HOLD_MS + ACTIVE_ENTRY_FADE_MS;
  }
  return null;
}

function sanitizeActivityEvent(event = {}) {
  const jobId = primitiveString(event.jobId || event.uploadJobId);
  const clientEntryId = primitiveString(event.clientEntryId);
  if (!jobId || !clientEntryId) {
    return null;
  }
  const totalBytes = primitiveNumber(event.totalBytes, primitiveNumber(event.size, 0));
  const uploadedBytes = primitiveNumber(event.uploadedBytes, 0);
  const progressPercent = primitiveNumber(
    event.progressPercent,
    totalBytes > 0 ? Math.round((Math.max(0, Math.min(totalBytes, uploadedBytes)) / totalBytes) * 100) : null
  );
  const status = normalizeEntryStatus(event.status || event.clientStatus);
  const timestamp = primitiveString(event.timestamp || event.lastUpdated || event.progressUpdatedAt || new Date().toISOString());
  const activeUpload = state.activeUpload;

  return {
    jobId,
    localUploadId: primitiveString(event.localUploadId || (
      activeUpload && (
        primitiveString(activeUpload.uploadJobId) === jobId ||
        primitiveString(activeUpload.retryOfJobId) === jobId
      ) ? activeUpload.localUploadId : ""
    ), null),
    clientEntryId,
    entryType: primitiveString(event.entryType, "FILE").toUpperCase() === "FOLDER" ? "FOLDER" : "FILE",
    relativePath: primitiveString(event.relativePath || event.path || event.name),
    name: primitiveString(event.name || event.relativePath || event.path),
    status,
    uploadedBytes: Math.max(0, uploadedBytes || 0),
    totalBytes: Math.max(0, totalBytes || 0),
    progressPercent: progressPercent === null ? null : Math.max(0, Math.min(100, progressPercent)),
    errorMessage: primitiveString(event.errorMessage || event.technicalMessage, ""),
    lastUpdated: timestamp,
    updatedAtMs: nowMs(),
  };
}

function statusRank(status) {
  if (status === "COMPLETE") {
    return -1;
  }
  if (status === "IN_PROGRESS") {
    return 0;
  }
  if (status === "PENDING" || status === "WAITING") {
    return 1;
  }
  return 2;
}

function activeRowsForJob(jobState, atMs = nowMs()) {
  const rows = [];
  jobState.rows.forEach((row) => {
    const holdMs = totalHoldMs(row.status);
    if (holdMs !== null && atMs - row.terminalAtMs >= holdMs) {
      jobState.rows.delete(activeEntryKey(row));
      return;
    }
    const fadeStartsAt = row.status === "COMPLETE"
      ? row.terminalAtMs
      : (ACTIVE_ENTRY_TERMINAL_STATUSES.has(row.status) ? row.terminalAtMs + ACTIVE_ENTRY_FAILURE_HOLD_MS : null);
    rows.push({
      jobId: row.jobId,
      localUploadId: row.localUploadId,
      clientEntryId: row.clientEntryId,
      entryType: row.entryType,
      relativePath: row.relativePath,
      name: row.name,
      status: row.status,
      uploadedBytes: row.uploadedBytes,
      totalBytes: row.totalBytes,
      progressPercent: row.progressPercent,
      errorMessage: row.errorMessage,
      lastUpdated: row.lastUpdated,
      fading: fadeStartsAt !== null && atMs >= fadeStartsAt,
    });
  });
  rows.sort((a, b) => statusRank(a.status) - statusRank(b.status) || String(a.relativePath || a.name).localeCompare(String(b.relativePath || b.name)));
  return rows;
}

function enforceJobRowCap(jobState) {
  if (jobState.rows.size <= ACTIVE_ENTRY_ROW_CAP_PER_JOB) {
    return;
  }
  const rows = Array.from(jobState.rows.values()).sort((a, b) => {
    const terminalDelta = Number(ACTIVE_ENTRY_TERMINAL_STATUSES.has(b.status)) - Number(ACTIVE_ENTRY_TERMINAL_STATUSES.has(a.status));
    return terminalDelta || a.updatedAtMs - b.updatedAtMs;
  });
  while (jobState.rows.size > ACTIVE_ENTRY_ROW_CAP_PER_JOB && rows.length) {
    const row = rows.shift();
    jobState.rows.delete(activeEntryKey(row));
  }
}

function publishActiveEntrySnapshot() {
  activeEntrySnapshotTimer = null;
  const atMs = nowMs();
  const jobs = {};
  let hasTerminalRowsOnHold = false;
  activeEntryJobs.forEach((jobState, jobId) => {
    const rows = activeRowsForJob(jobState, atMs);
    if (!rows.length) {
      activeEntryJobs.delete(jobId);
      return;
    }
    hasTerminalRowsOnHold = hasTerminalRowsOnHold || rows.some((row) => ACTIVE_ENTRY_TERMINAL_STATUSES.has(row.status));
    jobs[jobId] = {
      rows,
      totalCount: rows.length,
      activeCount: rows.filter((row) => !ACTIVE_ENTRY_TERMINAL_STATUSES.has(row.status)).length,
      pendingCount: rows.filter((row) => row.status === "PENDING" || row.status === "WAITING").length,
      inProgressCount: rows.filter((row) => row.status === "IN_PROGRESS").length,
      completeCount: rows.filter((row) => row.status === "COMPLETE").length,
      failedCount: rows.filter((row) => row.status !== "COMPLETE" && ACTIVE_ENTRY_TERMINAL_STATUSES.has(row.status)).length,
      updatedAt: new Date(atMs).toISOString(),
    };
  });
  state.activeEntryActivity = { jobs };
  if (hasTerminalRowsOnHold) {
    scheduleActiveEntrySnapshot();
  }
}

function scheduleActiveEntrySnapshot() {
  if (activeEntrySnapshotTimer || typeof window === "undefined") {
    return;
  }
  activeEntrySnapshotTimer = window.setTimeout(publishActiveEntrySnapshot, ACTIVE_ENTRY_SNAPSHOT_INTERVAL_MS);
}

function forceActiveEntrySnapshot() {
  if (activeEntrySnapshotTimer && typeof window !== "undefined") {
    window.clearTimeout(activeEntrySnapshotTimer);
    activeEntrySnapshotTimer = null;
  }
  publishActiveEntrySnapshot();
}

export function registerUploadManager(uploadManager) {
  manager = uploadManager;
}

export function unregisterUploadManager(uploadManager) {
  if (manager === uploadManager) {
    manager = null;
  }
}

export function getActiveUploadState() {
  return state.activeUpload;
}

export function hasActiveUpload() {
  return !!state.activeUpload && !ACTIVE_TERMINAL_STATUSES.has(state.activeUpload.status);
}

export function enqueueUpload(payload) {
  if (!manager) {
    throw new Error("Upload manager is not available");
  }
  if (hasActiveUpload()) {
    throw new Error("An upload is already in progress");
  }

  const job = {
    localUploadId: `local-upload-${nextLocalUploadId++}`,
    startedAt: new Date().toISOString(),
    folderId: payload.folderId,
    payload,
    status: "queued",
    totalFiles: payload.chosenFiles?.length || 0,
  };
  state.activeUpload = lightweightUploadState(job);
  manager.startQueuedUpload(job);
  return job.localUploadId;
}

export function updateActiveUpload(localUploadId, updates) {
  if (!state.activeUpload || state.activeUpload.localUploadId !== localUploadId) {
    return;
  }
  state.activeUpload = {
    ...state.activeUpload,
    ...updates,
  };
}

export async function requestUploadCancel(localUploadId = null) {
  if (!manager || !state.activeUpload) {
    return;
  }
  if (localUploadId && state.activeUpload.localUploadId !== localUploadId) {
    return;
  }
  updateActiveUpload(state.activeUpload.localUploadId, { status: "cancelling" });
  await manager.cancelActiveUpload();
}

export function clearActiveUpload(localUploadId) {
  if (!state.activeUpload || state.activeUpload.localUploadId !== localUploadId) {
    return;
  }
  state.activeUpload = null;
}

export function publishEntryActivity(event) {
  try {
    const row = sanitizeActivityEvent(event);
    if (!row) {
      return;
    }
    if (!activeEntryJobs.has(row.jobId)) {
      activeEntryJobs.set(row.jobId, { rows: new Map() });
    }
    const jobState = activeEntryJobs.get(row.jobId);
    const key = activeEntryKey(row);
    const previous = jobState.rows.get(key) || {};
    const nextStatus = row.status || previous.status || "IN_PROGRESS";
    const preserveTerminalProgress = ACTIVE_ENTRY_TERMINAL_STATUSES.has(nextStatus)
      && previous.uploadedBytes > row.uploadedBytes;
    const uploadedBytes = preserveTerminalProgress ? previous.uploadedBytes : row.uploadedBytes;
    const progressPercent = preserveTerminalProgress ? previous.progressPercent : row.progressPercent;
    const terminalAtMs = ACTIVE_ENTRY_TERMINAL_STATUSES.has(nextStatus)
      ? previous.terminalAtMs || row.updatedAtMs
      : null;
    jobState.rows.set(key, {
      ...previous,
      ...row,
      status: nextStatus,
      uploadedBytes,
      progressPercent,
      terminalAtMs,
    });
    enforceJobRowCap(jobState);
    scheduleActiveEntrySnapshot();
  } catch (error) {
    // Activity is best-effort. Upload execution must never depend on UI state.
  }
}

export function getActiveEntryActivitySnapshot(jobId = null) {
  if (!jobId) {
    return state.activeEntryActivity;
  }
  return state.activeEntryActivity.jobs?.[String(jobId)] || { rows: [], totalCount: 0 };
}

export function clearActiveEntryActivity(jobId = null) {
  if (jobId === null || jobId === undefined) {
    activeEntryJobs.clear();
  } else {
    activeEntryJobs.delete(String(jobId));
  }
  forceActiveEntrySnapshot();
}

export const uploadManagerState = state;
